import * as React from "react";
import { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";
import FacilityRow from "./FacilityRow";
import { visibleFacilityColumns } from "./FacilityGrid";

// The secondary Facilities lens (design §4, slice A7): the same facilities as the grouped view,
// flattened fleet-wide with a Franchise column and sortable headers.
//
// Purpose is fleet-wide ownership cleanup -- "every facility assigned to Josh Gibson", "sort by
// BDM to find the gaps" -- which the grouped view cannot answer because it orders by franchise.
// It reuses FacilityRow and every dialog unchanged; only the grouping and the extra column differ.
//
// NOT the default lens. Franchises remains the primary mental model.

const SORTABLE = {
  franchise: (facility) => String(facility.franchiseName || "").toLowerCase(),
  name: (facility) => String(facility.name || "").toLowerCase(),
  status: (facility) => (facility.isActive ? 0 : 1),
  // Facilities with no BDM sort last rather than first, so the gaps are not buried above the
  // configured rows when scanning by owner.
  bdm: (facility) => (facility.effectiveBdmName || "￿").toLowerCase(),
  pacesetter: (facility) => (facility.pacesetter == null ? 2 : facility.pacesetter === 1 ? 0 : 1),
  nll: (facility) => (facility.nextLevelLeader == null ? 2 : facility.nextLevelLeader === 1 ? 0 : 1),
  sites: (facility) => facility.siteCount,
};

export default function FacilityFlatList(props) {
  const franchises = props.franchises || [];
  const showActions = props.showActions;
  const hasFilters = props.hasFilters;

  const [orderBy, setOrderBy] = useState("franchise");
  const [direction, setDirection] = useState("asc");

  // Flatten, carrying the owning franchise down onto each row.
  const facilities = useMemo(() => {
    const flattened = [];
    for (const franchise of franchises) {
      for (const facility of franchise.facilities) {
        flattened.push({ ...facility, franchiseName: franchise.name, comcode: franchise.comcode });
      }
    }
    return flattened;
  }, [franchises]);

  const sorted = useMemo(() => {
    const keyOf = SORTABLE[orderBy] || SORTABLE.franchise;
    const factor = direction === "asc" ? 1 : -1;
    return [...facilities].sort((a, b) => {
      const left = keyOf(a);
      const right = keyOf(b);
      if (left < right) return -1 * factor;
      if (left > right) return 1 * factor;
      // Stable, predictable tiebreak so equal keys never appear to shuffle between renders.
      return String(a.id).localeCompare(String(b.id));
    });
  }, [facilities, orderBy, direction]);

  const handleSort = (columnId) => {
    if (!SORTABLE[columnId]) return;
    if (orderBy === columnId) {
      setDirection((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(columnId);
      setDirection("asc");
    }
  };

  if (facilities.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {hasFilters ? "No facilities match the current filters." : "No facilities to display."}
        </Typography>
      </Box>
    );
  }

  const columns = visibleFacilityColumns(showActions, true);

  const colElements = columns.map((column) => <col key={column.id} style={{ width: column.width }} />);

  const headCells = columns.map((column) => (
    <TableCell key={column.id} align={column.align} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
      {SORTABLE[column.id] ? (
        <TableSortLabel
          active={orderBy === column.id}
          direction={orderBy === column.id ? direction : "asc"}
          onClick={() => handleSort(column.id)}
        >
          {column.label}
        </TableSortLabel>
      ) : (
        column.label
      )}
    </TableCell>
  ));

  const rows = sorted.map((facility) => (
    <FacilityRow
      key={facility.id}
      facility={facility}
      franchiseName={facility.franchiseName}
      showActions={showActions}
      bdmOptions={props.bdmOptions}
      onSaveOwnership={props.onSaveOwnership}
      onSaveDetails={props.onSaveDetails}
      onSaveSite={props.onSaveSite}
      onDeleteSite={props.onDeleteSite}
      onSaved={props.onSaved}
    />
  ));

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <Table size="small" sx={{ tableLayout: "fixed" }}>
        <colgroup>{colElements}</colgroup>
        <TableHead>
          <TableRow>{headCells}</TableRow>
        </TableHead>
        <TableBody>{rows}</TableBody>
      </Table>
    </Paper>
  );
}
